import { useEffect, useLayoutEffect } from 'react';

import type { ThemeMode } from '@creditornot/alchemy-utils';
import { DATA_ATTR_THEME_MODE } from '@creditornot/alchemy-utils';

import { canUseDOM } from '../constants';

const useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;

interface Options {
  /**
   * Direction of the text, either `ltr` (left-to-right) or `rtl` (right-to-left).
   *
   * You may leave this empty if another part of your application is responsible for setting the
   * direction. When defined, the `dir` attribute will be set on the `<html>` element.
   */
  direction?: 'ltr' | 'rtl';
  /**
   * Language code as defined in [RFC 5646: Tags for Identifying Languages (also known as BCP
   * 47)](https://datatracker.ietf.org/doc/html/rfc5646).
   *
   * You may leave this empty if another part of your application is responsible for setting the
   * language. When defined, the `lang` attribute will be set on the `<html>` element.
   */
  language?: string;
  /**
   * Alchemy theme mode to set on the `<html>` element. Controls the `data-al-theme` attribute on
   * the `<html>` element.
   */
  themeMode: ThemeMode;
}

/**
 * Sets up attributes for the `documentElement` that the Alchemy library relies on for styling.
 */
export function useAlchemySetupEffect({ direction, language, themeMode }: Options) {
  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute(DATA_ATTR_THEME_MODE, themeMode);
  }, [themeMode]);

  useIsomorphicLayoutEffect(() => {
    if (direction) {
      document.documentElement.setAttribute('dir', direction);
    }
  }, [direction]);

  useIsomorphicLayoutEffect(() => {
    if (language) {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);
}
